/**
 * Finanzas → **Ingresos** y **Egresos administrativos**
 * (`/finanzas/ingresos` y `/finanzas/egresos`).
 *
 * Portado de las dos pantallas del panel anterior (`Administrative_incomes` y
 * `Administrative_expenses`, con sus vistas `finance/administrative_*`), que
 * compartían todo menos el tipo de movimiento, así que aquí es **una sola
 * pantalla** con su `tipo`.
 *
 * Muestra el resumen del filtro (total y número de registros), los filtros de
 * fechas, tipo y cuenta con su buscador, y el listado de movimientos con sus
 * acciones: ver la ficha, editar, abrir el comprobante y anular.
 *
 * Reglas heredadas:
 * - Cada pantalla lista sólo su tipo y muestra también los **anulados**, con su
 *   distintivo (y sin editar ni anular).
 * - En los **egresos** el comprobante es obligatorio.
 * - Anular es una baja lógica.
 */

import {
  Ban,
  FileSpreadsheet,
  FileText,
  Pencil,
  PlusCircle,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatCurrency, formatDateValue, toIsoDate } from '@/lib/utils';

import {
  administrativosApi,
  useActualizarAdministrativo,
  useAdministrativos,
  useAnularAdministrativo,
  useDetalleAdministrativo,
  useRegistrarAdministrativo,
} from '../administrativos.api';
import {
  DialogoAdministrativo,
  DialogoDetalleAdministrativo,
} from '../components/administrativo-dialogos';
import type {
  FiltrosAdministrativos,
  MovimientoAdministrativo,
  TipoAdministrativo,
} from '../administrativos.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const TAMANOS = [10, 25, 50, 100];

/** Primer día del mes en curso. */
function inicioDeMes(): string {
  const hoy = new Date();

  return toIsoDate(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
}

interface Props {
  tipo: TipoAdministrativo;
}

export function AdministrativosPage({ tipo }: Props) {
  const esIngreso = tipo === 'ingresos';

  useDocumentTitle(
    esIngreso ? 'Finanzas · Ingresos administrativos' : 'Finanzas · Egresos administrativos',
  );

  const [desde, setDesde] = useState(inicioDeMes);
  const [hasta, setHasta] = useState(() => toIsoDate(new Date()));
  const [subtipo, setSubtipo] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(10);
  const [formulario, setFormulario] = useState(false);
  const [editando, setEditando] = useState<MovimientoAdministrativo | null>(null);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [porAnular, setPorAnular] = useState<MovimientoAdministrativo | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<FiltrosAdministrativos>(
    () => ({
      desde,
      hasta,
      subtipo,
      cuenta,
      buscar: busqueda.trim(),
      page: pagina,
      perPage: limite,
    }),
    [desde, hasta, subtipo, cuenta, busqueda, pagina, limite],
  );

  const consulta = useAdministrativos(tipo, filtros);
  const detalle = useDetalleAdministrativo(tipo, detalleId);
  const registrar = useRegistrarAdministrativo(tipo);
  const actualizar = useActualizarAdministrativo(tipo);
  const anular = useAnularAdministrativo(tipo);

  const datos = consulta.data;
  const movimientos = datos?.movimientos ?? [];
  const totales = datos?.totales;
  const total = datos?.total ?? 0;
  const paginas = datos?.paginas ?? 1;
  const guardando = registrar.isPending || actualizar.isPending;

  /** Cambiar cualquier filtro vuelve a la primera página. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    cambio();
  };

  const avisar = (mensaje: string) => {
    setAviso(mensaje);
    setError(null);
    setTimeout(() => setAviso(null), 6000);
  };

  const exportar = async (formato: 'csv' | 'pdf') => {
    setError(null);

    try {
      await abrirPdfAutenticado(administrativosApi.urlExportar(tipo, filtros, formato), {
        nombre: `${tipo}-administrativos-${desde}_${hasta}.${formato}`,
      });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo generar la exportación.');
    }
  };

  const abrirComprobante = async (movimiento: MovimientoAdministrativo) => {
    if (!movimiento.comprobante) return;

    setError(null);

    try {
      await abrirPdfAutenticado(administrativosApi.urlComprobante(tipo, movimiento.id), {
        nombre: movimiento.comprobante.archivo,
      });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo abrir el comprobante.');
    }
  };

  const confirmarAnulacion = () => {
    if (!porAnular) return;

    setError(null);
    anular.mutate(porAnular.id, {
      onSuccess: (respuesta) => {
        avisar(respuesta.message);
        setPorAnular(null);
      },
      onError: (fallo) => {
        setError(fallo instanceof Error ? fallo.message : 'No se pudo anular el movimiento.');
        setPorAnular(null);
      },
    });
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">
            {esIngreso ? 'Ingresos administrativos' : 'Egresos administrativos'}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            {esIngreso
              ? 'Dinero que entra a las cuentas sin ser una venta: préstamos recibidos, intereses, alquileres cobrados, ingresos diversos.'
              : 'Dinero que sale de las cuentas de la empresa: planilla, servicios, alquileres, impuestos, honorarios y demás gastos administrativos.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileSpreadsheet className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('csv')}
          >
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('pdf')}
          >
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
          <Button
            size="sm"
            leftIcon={<PlusCircle className="size-4" aria-hidden="true" />}
            onClick={() => {
              setError(null);
              setEditando(null);
              setFormulario(true);
            }}
          >
            Nuevo {esIngreso ? 'ingreso' : 'egreso'}
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo leer el listado">
          Vuelve a intentarlo en unos segundos.
        </Alert>
      ) : null}

      {/* Resumen del filtro */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card className={esIngreso ? 'border-emerald-200 bg-emerald-50/60' : 'border-rose-200 bg-rose-50/60'}>
          <CardContent className="flex flex-col gap-1 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
              Total {esIngreso ? 'ingresos' : 'egresos'} del rango
            </p>
            <p
              className={cn(
                'text-base font-bold break-words tabular-nums sm:text-lg',
                esIngreso ? 'text-emerald-700' : 'text-rose-700',
              )}
            >
              {formatCurrency(totales?.total ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-1 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
              Registros
            </p>
            <p className="text-base font-bold text-ink-800 tabular-nums sm:text-lg">
              {(totales?.registros ?? 0).toLocaleString('es-PE')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-1 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
              Anulados
            </p>
            <p className="text-base font-bold text-ink-500 tabular-nums sm:text-lg">
              {(totales?.anulados ?? 0).toLocaleString('es-PE')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Desde
              <CampoFecha
                tipo="date"
                value={desde}
                max={hasta}
                onChange={(evento) => filtrar(() => setDesde(evento.target.value))}
                aria-label="Fecha de inicio"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Hasta
              <CampoFecha
                tipo="date"
                value={hasta}
                min={desde}
                onChange={(evento) => filtrar(() => setHasta(evento.target.value))}
                aria-label="Fecha de fin"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Tipo de {esIngreso ? 'ingreso' : 'egreso'}
              <Select
                value={subtipo}
                onChange={(evento) => filtrar(() => setSubtipo(evento.target.value))}
                className={CONTROL}
                aria-label="Tipo de movimiento"
              >
                <option value="">Todos</option>
                {(datos?.subtipos ?? []).map((opcion) => (
                  <option key={opcion.id} value={String(opcion.id)}>
                    {opcion.nombre}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Cuenta bancaria
              <Select
                value={cuenta}
                onChange={(evento) => filtrar(() => setCuenta(evento.target.value))}
                className={CONTROL}
                aria-label="Cuenta bancaria"
              >
                <option value="">Todas</option>
                {(datos?.cuentas ?? []).map((opcion) => (
                  <option key={opcion.id} value={String(opcion.id)}>
                    {opcion.cuenta} - {opcion.banco}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs font-medium text-ink-500">
              Buscar
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(evento) => filtrar(() => setBusqueda(evento.target.value))}
                  placeholder="Descripción, referencia, cuenta, método, ejecutor…"
                  className="pl-9"
                  aria-label="Buscar movimiento"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
                className={cn(CONTROL, 'w-24')}
                aria-label="Filas por página"
              >
                {TAMANOS.map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {total.toLocaleString('es-PE')} movimiento{total === 1 ? '' : 's'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Listado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">#</th>
                  <th className="px-2 py-2 whitespace-nowrap">Fecha</th>
                  <th className="px-2 py-2 whitespace-nowrap">Estado</th>
                  <th className="px-2 py-2">Tipo</th>
                  <th className="px-2 py-2">Descripción</th>
                  <th className="px-2 py-2">Banco / Cuenta</th>
                  <th className="px-2 py-2">Método</th>
                  <th className="px-2 py-2">Referencia</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Monto</th>
                  <th className="px-2 py-2 whitespace-nowrap">Registró</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 5 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 11 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : movimientos.map((fila) => (
                      <tr
                        key={fila.id}
                        className={cn('border-t border-ink-100', fila.anulado && 'text-ink-500')}
                      >
                        <td className="px-2 py-2 text-ink-500 tabular-nums">{fila.id}</td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          {fila.fecha ? formatDateValue(fila.fecha) : '—'}
                        </td>

                        <td className="px-2 py-2">
                          <Badge variant={fila.anulado ? 'neutral' : 'success'}>
                            {fila.anulado ? 'Anulado' : 'Registrado'}
                          </Badge>
                          {fila.anulado && fila.anulado_por ? (
                            <p className="mt-1 text-[11px] text-ink-500">
                              por {fila.anulado_por}
                            </p>
                          ) : null}
                        </td>

                        <td className="px-2 py-2">
                          <Badge variant={esIngreso ? 'success' : 'danger'}>{fila.subtipo}</Badge>
                        </td>

                        <td className="max-w-[18rem] px-2 py-2">
                          <span className="block truncate" title={fila.descripcion ?? ''}>
                            {fila.descripcion || '—'}
                          </span>
                          {fila.observaciones ? (
                            <span
                              className="block truncate text-[11px] text-ink-500"
                              title={fila.observaciones}
                            >
                              {fila.observaciones}
                            </span>
                          ) : null}
                        </td>

                        <td className="px-2 py-2">
                          <p className="font-medium text-ink-800">{fila.banco}</p>
                          <p className="text-[11px] text-ink-500">{fila.cuenta}</p>
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">{fila.metodo_pago}</td>

                        <td className="max-w-[12rem] px-2 py-2">
                          <span className="block truncate" title={fila.referencia ?? ''}>
                            {fila.referencia || '—'}
                          </span>
                        </td>

                        <td
                          className={cn(
                            'px-2 py-2 text-right font-semibold whitespace-nowrap tabular-nums',
                            fila.anulado
                              ? 'text-ink-400 line-through'
                              : esIngreso
                                ? 'text-emerald-700'
                                : 'text-rose-600',
                          )}
                        >
                          {formatCurrency(fila.monto)}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          <p className="text-ink-700">{fila.ejecutor}</p>
                          <p className="text-[11px] text-ink-500">{fila.registrado ?? '—'}</p>
                        </td>

                        <td className="px-2 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Ver la ficha"
                              aria-label={`Ver la ficha del movimiento ${fila.id}`}
                              onClick={() => setDetalleId(fila.id)}
                            >
                              <FileText className="size-4" aria-hidden="true" />
                            </Button>

                            {fila.comprobante ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title={
                                  fila.comprobante.disponible
                                    ? 'Abrir el comprobante'
                                    : 'El archivo ya no está en el servidor'
                                }
                                aria-label={`Abrir el comprobante del movimiento ${fila.id}`}
                                disabled={!fila.comprobante.disponible}
                                onClick={() => void abrirComprobante(fila)}
                              >
                                <FileSpreadsheet className="size-4 text-brand-600" aria-hidden="true" />
                              </Button>
                            ) : null}

                            {fila.puede_editar ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Editar"
                                aria-label={`Editar el movimiento ${fila.id}`}
                                onClick={() => {
                                  setError(null);
                                  setEditando(fila);
                                  setFormulario(true);
                                }}
                              >
                                <Pencil className="size-4 text-amber-600" aria-hidden="true" />
                              </Button>
                            ) : null}

                            {fila.puede_anular ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Anular"
                                aria-label={`Anular el movimiento ${fila.id}`}
                                onClick={() => {
                                  setError(null);
                                  setPorAnular(fila);
                                }}
                              >
                                <Ban className="size-4 text-rose-600" aria-hidden="true" />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>

              <tfoot className="bg-ink-50/60 font-semibold text-ink-800">
                <tr className="border-t border-ink-100">
                  <td className="px-2 py-2 text-right" colSpan={8}>
                    Total del filtro
                  </td>
                  <td
                    className={cn(
                      'px-2 py-2 text-right whitespace-nowrap tabular-nums',
                      esIngreso ? 'text-emerald-700' : 'text-rose-600',
                    )}
                  >
                    {formatCurrency(totales?.total ?? 0)}
                  </td>
                  <td className="px-2 py-2" colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                onClick={() => setPagina((actual) => Math.min(paginas, actual + 1))}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diálogos */}
      {formulario ? (
        <DialogoAdministrativo
          abierto
          tipo={tipo}
          movimiento={editando}
          hoy={datos?.hoy ?? toIsoDate(new Date())}
          subtipos={datos?.subtipos ?? []}
          cuentas={datos?.cuentas ?? []}
          metodos={datos?.metodos_pago ?? []}
          archivoObligatorio={datos?.archivo_obligatorio ?? !esIngreso}
          guardando={guardando}
          error={error}
          onCerrar={() => {
            setFormulario(false);
            setEditando(null);
          }}
          onGuardar={(nuevos) => {
            setError(null);

            if (editando) {
              actualizar.mutate(
                { id: editando.id, datos: nuevos },
                {
                  onSuccess: (respuesta) => {
                    avisar(respuesta.message);
                    setFormulario(false);
                    setEditando(null);
                  },
                  onError: (fallo) =>
                    setError(
                      fallo instanceof Error
                        ? fallo.message
                        : 'No se pudo actualizar el movimiento.',
                    ),
                },
              );

              return;
            }

            registrar.mutate(nuevos, {
              onSuccess: (respuesta) => {
                avisar(respuesta.message);
                setFormulario(false);
              },
              onError: (fallo) =>
                setError(
                  fallo instanceof Error ? fallo.message : 'No se pudo registrar el movimiento.',
                ),
            });
          }}
        />
      ) : null}

      <DialogoDetalleAdministrativo
        abierto={Boolean(detalleId)}
        tipo={tipo}
        consulta={detalle.data}
        cargando={detalle.isLoading}
        onCerrar={() => setDetalleId(null)}
      />

      <ConfirmDialog
        open={Boolean(porAnular)}
        title={`¿Anular el movimiento #${porAnular?.id ?? ''}?`}
        description="El movimiento quedará marcado como anulado y seguirá visible en el listado, pero ya no sumará al total del rango."
        confirmLabel="Anular"
        loading={anular.isPending}
        onConfirm={confirmarAnulacion}
        onClose={() => setPorAnular(null)}
      />
    </div>
  );
}
